import React, { useState } from 'react';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Link
} from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { FirebaseConfig } from '../../data/firebaseConfig';
import { toast } from 'react-toastify';

const SignUpPage: React.FC = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        organizationName: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const navigate = useNavigate();
    const functions = getFunctions(FirebaseConfig.app);
    const signUp = httpsCallable(functions, 'signUp');

    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        setError(null);
    };

    const validateForm = () => {
        if (!formData.email || !formData.password || !formData.firstName ||
            !formData.lastName || !formData.organizationName) {
            setError('All fields are required.');
            return false;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match.');
            return false;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long.');
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address.');
            return false;
        }

        return true;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Create account via Firebase function
            const result = await signUp({
                email: formData.email,
                password: formData.password,
                firstName: formData.firstName,
                lastName: formData.lastName,
                organizationName: formData.organizationName
            });

            console.log('Account created:', result.data);

            const app = FirebaseConfig.app;
            const auth = getAuth(app);
            // Sign in the user immediately after account creation
            await signInWithEmailAndPassword(auth, formData.email, formData.password);

            toast.success('Account created successfully!');
            navigate('/dashboard');

        } catch (error: any) {
            console.error('Error creating account:', error);

            let errorMessage = 'Failed to create account. Please try again.';

            if (error.code === 'auth/email-already-in-use') {
                errorMessage = 'An account with this email address already exists.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSignInClick = () => {
        navigate('/signin');
    };

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
                    <Typography component="h1" variant="h4" align="center" gutterBottom>
                        Create Your Account
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
                        Set up your organization and start managing your campaigns
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                required
                                fullWidth
                                label="First Name"
                                value={formData.firstName}
                                onChange={handleInputChange('firstName')}
                                disabled={loading}
                            />
                            <TextField
                                required
                                fullWidth
                                label="Last Name"
                                value={formData.lastName}
                                onChange={handleInputChange('lastName')}
                                disabled={loading}
                            />
                        </Box>

                        <TextField
                            required
                            fullWidth
                            label="Organization Name"
                            value={formData.organizationName}
                            onChange={handleInputChange('organizationName')}
                            margin="normal"
                            disabled={loading}
                            helperText="This will be the name of your organization in the system"
                        />

                        <TextField
                            required
                            fullWidth
                            label="Email Address"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange('email')}
                            margin="normal"
                            disabled={loading}
                            autoComplete="email"
                        />

                        <TextField
                            required
                            fullWidth
                            label="Password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange('password')}
                            margin="normal"
                            disabled={loading}
                            helperText="Must be at least 6 characters long"
                        />

                        <TextField
                            required
                            fullWidth
                            label="Confirm Password"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange('confirmPassword')}
                            margin="normal"
                            disabled={loading}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={loading}
                            size="large"
                        >
                            {loading ? <CircularProgress size={24} /> : 'Create Account'}
                        </Button>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2">
                                Already have an account?{' '}
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={handleSignInClick}
                                    sx={{ textDecoration: 'none' }}
                                >
                                    Sign in here
                                </Link>
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default SignUpPage;